import { useEffect, useState } from 'react';
import {
  leavePresence,
  PresenceScopeType,
  PresenceUser,
  sendPresenceHeartbeat,
} from '../modules/presence/api/presenceApi';

// Heartbeat cadence. Must stay well under the backend freshness window (180s)
// so an open page never flickers out between beats.
const HEARTBEAT_MS = 20000;

/**
 * Live "who else is here" for a scope. Heartbeats while the tab is VISIBLE only
 * (a hidden/background tab makes no requests), and best-effort leaves on unmount
 * or when the scope key changes. Pass a falsy `scopeKey` to disable (e.g. before
 * a customer is selected). Presence is best-effort: failures never surface.
 */
export function usePresence(
  scopeType: PresenceScopeType,
  scopeKey: string | null | undefined
): PresenceUser[] {
  const [others, setOthers] = useState<PresenceUser[]>([]);

  useEffect(() => {
    if (!scopeKey) {
      setOthers([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const beat = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const list = await sendPresenceHeartbeat(scopeType, scopeKey);
        if (!cancelled) setOthers(list);
      } catch {
        // Presence is best-effort — never surface an error to the user.
      }
    };

    const start = () => {
      if (timer) return;
      void beat();
      timer = setInterval(beat, HEARTBEAT_MS);
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      // Leave the scope captured by THIS effect run (old key on a re-key).
      void leavePresence(scopeType, scopeKey).catch(() => {});
      setOthers([]);
    };
  }, [scopeType, scopeKey]);

  return others;
}
